import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  createPurchase,
  getParties,
  getSinglePurchase,
  updatePurchase,
  getItems,
  addItem,
  getStockTransaction,
  searchProduct,
  getUnits,
  getAllExpenses,
} from "../../services/services";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { toastError, toastSuccess } from "../../utils";
import Loading from "../../components/loading";
import PartyAutoSuggest from "./PartyAutoSuggest";
import ItemsSection from "./ItemsSection";
import AddPartyModal from "../Modal/AddPartyModal";

const CreatePurchasePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [party, setParty] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partyId, setPartyId] = useState("");
  const [originalItems, setOriginalItems] = useState([]);
  const [partyName, setPartyName] = useState("");
  const [isAddingParty, setIsAddingParty] = useState(false);
  const [stockData, setStockData] = useState({});
  const [units, setUnits] = useState([]);
  const [savedExpenses, setSavedExpenses] = useState([]);
  const user = JSON.parse(localStorage.getItem("Login_User")) || {};
  const { _id } = user;

  const [formData, setFormData] = useState(() => {
    if (!id && _id) {
      const savedData = localStorage.getItem(`pendingPurchase_${_id}`);
      if (savedData) {
        return JSON.parse(savedData);
      }
    }
    return {
      paymentMode: "cash",
      totalAmount: 0,
      paidAmount: 0,
      dueAmount: 0,
      otherExpenses: [],
      items: [],
    };
  });

  useEffect(() => {
    if (!id && _id) {
      localStorage.setItem(`pendingPurchase_${_id}`, JSON.stringify(formData));
    }
  }, [formData, id, _id]);

  const handleNewItemAdded = (newItem) => {
    setAllItems((prev) => {
      const updatedItems = [...prev, newItem];
      setCategories([
        ...new Set(
          updatedItems.map((item) => item.category).filter((cat) => cat),
        ),
      ]);
      return updatedItems;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch parties
        const partyResponse = await getParties({ _id });
        const fetchedParties = partyResponse?.data?.response?.parties || [];
        setParty(fetchedParties);

        // Fetch items
        const itemResponse = await getItems();
        const fetchedItems = (itemResponse?.data?.response?.products || []).map(
          (p) => ({
            ...p,
            id: p._id,
            name: p.name || "Unnamed Item",
          }),
        );
        setAllItems(fetchedItems);

        // Fetch units
        // const unitResponse = await getUnits({});

        // console.log("Unit API Response:", unitResponse.data);

        // const responseData = unitResponse?.data?.response;

        // const fetchedUnits = Array.isArray(responseData)
        //   ? responseData
        //   : responseData?.units || [];

        // setUnits(fetchedUnits);

        // Fetch units
        const unitResponse = await getUnits({});

        const fetchedUnits = unitResponse?.data?.response?.units || [];

        setUnits(fetchedUnits);

        const expenseResponse = await getAllExpenses({});

        const fetchedExpenses = expenseResponse?.data?.response?.expenses ?? [];

        setSavedExpenses(fetchedExpenses);

        const uniqueCategories = [
          ...new Set(
            fetchedItems.map((item) => item.category).filter((cat) => cat),
          ),
        ];
        setCategories(uniqueCategories);

        // If editing existing purchase
        if (id) {
          const response = await getSinglePurchase(id);

          const singlePurchase = response?.data?.response?.singlePurchase || {};
          console.log("Full Purchase:", singlePurchase);
          console.log("Other Expenses:", singlePurchase.otherExpenses);
          const vendorId = singlePurchase.vendor?._id || "";
          setPartyId(vendorId);

          const selectedParty = fetchedParties.find(
            (p) => p._id === vendorId,
          ) || {
            name: singlePurchase.vendor?.name || "",
          };
          setPartyName(selectedParty.name);

          const items = (singlePurchase.items || []).map((item) => ({
            _id: item._id,
            productId: item.productId || "",
            name: item.itemName || "",
            quantity: item.quantity || 0,
            unit: item.unit || "pcs",
            purchasePrice: item.purchasePrice || 0,

            // purchasePriceType: item.purchasePriceType || "withTax",
            purchasePriceType:
              item.purchasePriceType === "withTax"
                ? "inclusive"
                : item.purchasePriceType === "withoutTax"
                  ? "exclusive"
                  : item.purchasePriceType || "exclusive",

            salePrice: item.salePrice || 0,
            salePriceType: item.salePriceType || "withTax",
            minStockAlert: item.minStockAlert ?? 0,
            category: item.category || "",
            gstRate: item.gstRate || 0,
            purchaseDate: item.purchaseDate || new Date().toISOString(),
            expiryDate: item.expiryDate
              ? new Date(item.expiryDate).toISOString().split("T")[0]
              : "",
            party: vendorId || "",
          }));

          setFormData({
            vendor: vendorId || "",
            partyName: selectedParty.name || "",
            items,
            totalAmount: singlePurchase.totalAmount || 0,
            paidAmount: singlePurchase.paidAmount || 0,
            dueAmount: singlePurchase.dueAmount || 0,
            otherExpenses: singlePurchase.otherExpenses || [],
            paymentMode: singlePurchase.paymentMode || "cash",
          });
          setOriginalItems(items);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toastError(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Failed to fetch data",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, _id]);
  const loadProductOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 2) {
      return [];
    }

    try {
      const res = await searchProduct({
        search: inputValue.trim(),
      });

      const products =
        res?.data?.response?.products ?? res?.data?.response ?? [];

      if (!Array.isArray(products)) {
        return [];
      }
      return products.map((product) => ({
        label: product.name,
        value: product._id,
        raw: product,
      }));
    } catch (error) {
      console.error("Product search failed:", error);
      return [];
    }
  };

  // const calculateTaxInclusivePrice = (price, gstRate, priceType) => {
  //   const parsedPrice = parseFloat(price) || 0;
  //   if (priceType === "withTax") {
  //     return parsedPrice;
  //   }
  //   return parsedPrice * (1 + gstRate / 100);
  // };

  const calculateTaxInclusivePrice = (price, gstRate, priceType) => {
    const amount = Number(price) || 0;
    const rate = Number(gstRate) || 0;

    if (priceType === "inclusive" || priceType === "withTax") {
      return amount;
    }

    return amount + (amount * rate) / 100;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ["paidAmount"];
    const parsedValue = numericFields.includes(name)
      ? value === ""
        ? ""
        : parseFloat(value) >= 0
          ? parseFloat(value)
          : ""
      : value;

    setFormData((prev) => {
      const updated = { ...prev, [name]: parsedValue };

      const itemsTotal = prev.items.reduce((sum, item) => {
        const price = Number(item.purchasePrice) || 0;
        const quantity = Number(item.quantity) || 0;
        const gstRate = Number(item.gstRate) || 0;

        const priceType = item.purchasePriceType || "exclusive";

        const totalPrice =
          priceType === "inclusive" || priceType === "withTax"
            ? price
            : price + (price * gstRate) / 100;

        return sum + quantity * totalPrice;
      }, 0);

      // const itemsTotal = prev.items.reduce((sum, item) => {
      //   const price = Number(item.purchasePrice) || 0;
      //   const quantity = Number(item.quantity) || 0;
      //   const gstRate = Number(item.gstRate) || 0;
      //   const priceType = item.purchasePriceType || "withTax";

      //   const itemTotal =
      //     priceType === "withTax"
      //       ? price * quantity
      //       : price * quantity * (1 + gstRate / 100);

      //   return sum + itemTotal;
      // }, 0);

      // const itemsTotal = prev.items.reduce((sum, item) => {
      //   const priceWithTax = calculateTaxInclusivePrice(
      //     item.purchasePrice,
      //     item.gstRate,
      //     item.purchasePriceType,
      //   );
      //   return sum + item.quantity * priceWithTax;
      // }, 0);
      // const expensesTotal = prev.otherExpenses.reduce(
      //   (sum, expense) => sum + (parseFloat(expense.price) || 0),
      //   0,
      // );
      // updated.totalAmount = itemsTotal + expensesTotal;

      const expensesTotal = prev.otherExpenses.reduce((sum, expense) => {
        const price = Number(expense.price) || 0;
        const gstRate = Number(expense.gstRate) || 0;
        const taxType = expense.taxType || "exclusive";

        const gstAmount =
          taxType === "exclusive"
            ? (price * gstRate) / 100
            : price - price / (1 + gstRate / 100);

        const totalAmount = taxType === "exclusive" ? price + gstAmount : price;

        return sum + totalAmount;
      }, 0);

      updated.totalAmount = itemsTotal + expensesTotal;
      updated.dueAmount =
        updated.totalAmount -
        (parseFloat(name === "paidAmount" ? parsedValue : prev.paidAmount) ||
          0);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!partyId || !/^[0-9a-fA-F]{24}$/.test(partyId)) {
      toastError("Please select a party");
      return;
    }
    if (formData.items?.length === 0) {
      toastError("At least one item is required");
      return;
    }
    if (
      formData.items.some(
        (item) => !item.productId || !/^[0-9a-fA-F]{24}$/.test(item.productId),
      )
    ) {
      toastError("All items must have a valid product ID");
      return;
    }

    const invalidItems = formData.items.filter(
      (item) =>
        !item.name || !item.unit || !item.quantity || !item.purchasePrice,
    );
    if (invalidItems.length > 0) {
      console.warn("Invalid items in formData:", invalidItems);
      toastError(
        "All items must have name, unit, quantity, and purchase price",
      );
      return;
    }

    const payload = {
      vendor: partyId,
      items: formData.items.map((item) => ({
        _id: item._id || undefined,
        productId: item.productId,
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        purchasePrice: item.purchasePrice,
        purchasePriceType: item.purchasePriceType,
        salePrice: item.salePrice,
        salePriceType: item.salePriceType,
        minStockAlert: item.minStockAlert,
        category: item.category,
        gstRate: item.gstRate,
        purchaseDate: item.purchaseDate || new Date().toISOString(),
        expiryDate: item.expiryDate || null,
        party: item.party || partyId,
        batchCode: item.batchCode || undefined,
      })),
      totalAmount: formData.totalAmount,
      paidAmount: parseFloat(formData.paidAmount) || 0,
      dueAmount: formData.dueAmount,
      // otherExpenses: formData.otherExpenses.map((exp) => ({
      //   name: exp.name,
      //   price: exp.price,

      // })),

      otherExpenses: formData.otherExpenses.map((exp) => {
        const price = Number(exp.price) || 0;
        const gstRate = Number(exp.gstRate) || 0;
        const taxType = exp.taxType || "exclusive";

        const gstAmount =
          taxType === "exclusive"
            ? (price * gstRate) / 100
            : price - price / (1 + gstRate / 100);

        const totalAmount = taxType === "exclusive" ? price + gstAmount : price;

        return {
          name: exp.name,
          price,
          gstRate,
          taxType,
          gstAmount: Number(gstAmount.toFixed(2)),
          totalAmount: Number(totalAmount.toFixed(2)),
        };
      }),
      paymentMode: formData.paymentMode,
    };

    try {
      const response = await (id
        ? updatePurchase(id, payload)
        : createPurchase(payload));
      toastSuccess(response.data.message);
      if (!id && _id) {
        localStorage.removeItem(`pendingPurchase_${_id}`);
      }
      navigate("/items");
    } catch (error) {
      console.error("Error creating/updating purchase:", error);
      toastError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to save purchase",
      );
    }
  };

  const handleCancel = () => {
    if (!id && _id) {
      localStorage.removeItem(`pendingPurchase_${_id}`);
    }
    navigate("/items");
  };

  return (
    <div className="purchase-page" id="main">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0 custom-title">
          {id ? "Update" : "Create"} Purchase
        </h4>
        <div className="col-md-2 d-flex align-items-end justify-content-end">
          <button
            className="btn btn-custom"
            type="button"
            onClick={() => setIsAddingParty(true)}
          >
            Add Party
          </button>
        </div>
      </div>
      {isAddingParty && (
        <AddPartyModal
          isOpen={isAddingParty}
          show={isAddingParty}
          onClose={() => setIsAddingParty(false)}
          onPartyAdded={(newParty) => {
            setParty((prev) => [...prev, newParty]);
            setPartyId(newParty._id);
            setPartyName(newParty.name);
          }}
        />
      )}

      {loading ? (
        <Loading />
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="card mb-4">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3 saleform">
                  <label htmlFor="partyName" className="form-label">
                    Party Name <span style={{ color: "red" }}>*</span>
                  </label>

                  {/* <PartyAutoSuggest
                    party={party.filter((p) =>
                      ["vendor", "both"].includes(p.partyType?.toLowerCase()),
                    )}
                    selectedPartyId={partyId}
                    selectedPartyName={partyName}
                    onSelect={(selected) => {
                      setPartyId(selected._id);
                      setPartyName(selected.name);
                    }}
                  /> */}

                  <PartyAutoSuggest
                    party={party}
                    selectedPartyId={partyId}
                    selectedPartyName={partyName}
                    onSelect={(selected) => {
                      setPartyId(selected._id);
                      setPartyName(selected.name);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <ItemsSection
            formData={formData}
            setFormData={setFormData}
            allItems={allItems}
            categories={categories}
            calculateTaxInclusivePrice={calculateTaxInclusivePrice}
            partyId={partyId}
            onNewItemAdded={handleNewItemAdded}
            stockData={stockData}
            loadProductOptions={loadProductOptions}
            units={units}
            savedExpenses={savedExpenses}
          />

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Payment Summary</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="mb-3 saleform">
                    <label htmlFor="paidAmount" className="form-label">
                      Amount Paid (₹)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="paidAmount"
                      name="paidAmount"
                      min="0"
                      step="1"
                      value={formData.paidAmount}
                      onChange={handleInputChange}
                      placeholder="Enter amount paid"
                    />
                  </div>
                  {/* <div className="d-flex justify-content-between">
                    <span className="all-span">Items Total:</span>
                    <span>
                      ₹{" "}
                      {formData.items
                        .reduce((sum, item) => {
                          const priceWithTax = calculateTaxInclusivePrice(
                            item.purchasePrice,
                            item.gstRate,
                            item.purchasePriceType,
                          );
                          return sum + item.quantity * priceWithTax;
                        }, 0)
                        .toFixed(2)}
                    </span>
                  </div> */}
                  {/* <div className="d-flex justify-content-between">
                    <span className="all-span">Items Total:</span>
                    <span>
                      ₹{" "}
                      {formData.items
                        .reduce((sum, item) => {
                          const price = Number(item.purchasePrice) || 0;
                          const quantity = Number(item.quantity) || 0;
                          const gstRate = Number(item.gstRate) || 0;
                          const priceType = item.purchasePriceType || "withTax";

                          const itemTotal =
                            priceType === "withTax"
                              ? price * quantity
                              : price * quantity * (1 + gstRate / 100);

                          return sum + itemTotal;
                        }, 0)
                        .toFixed(2)}
                    </span>
                  </div> */}

                  <div className="d-flex justify-content-between">
                    <span className="all-span">Items Total:</span>
                    <span>
                      ₹{" "}
                      {formData.items
                        .reduce((sum, item) => {
                          const price = Number(item.purchasePrice) || 0;
                          const quantity = Number(item.quantity) || 0;
                          const gstRate = Number(item.gstRate) || 0;
                          const priceType =
                            item.purchasePriceType || "exclusive";

                          const totalPrice =
                            priceType === "inclusive" || priceType === "withTax"
                              ? price
                              : price + (price * gstRate) / 100;

                          return sum + quantity * totalPrice;
                        }, 0)
                        .toFixed(2)}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between">
                    <span className="all-span">Other Expenses:</span>
                    <span>
                      {/* ₹{" "}
                      {formData.otherExpenses
                        .reduce(
                          (sum, expense) =>
                            sum + (parseFloat(expense.price) || 0),
                          0,
                        )
                        .toFixed(2)} */}

                      {formData.otherExpenses
                        .reduce((sum, expense) => {
                          const price = Number(expense.price) || 0;
                          const gstRate = Number(expense.gstRate) || 0;
                          const taxType = expense.taxType || "exclusive";

                          const gstAmount =
                            taxType === "exclusive"
                              ? (price * gstRate) / 100
                              : price - price / (1 + gstRate / 100);

                          return (
                            sum +
                            (taxType === "exclusive"
                              ? price + gstAmount
                              : price)
                          );
                        }, 0)
                        .toFixed(2)}
                    </span>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between mt-2 mb-3 fw-bold">
                    <span className="all-span">Total Amount:</span>
                    <span>₹ {(formData.totalAmount ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="all-span">Due Balance:</span>
                    <span
                      className={
                        formData.dueAmount === 0
                          ? "text-muted"
                          : formData.dueAmount > 0
                            ? "text-danger fw-semibold"
                            : "text-success fw-semibold"
                      }
                    >
                      ₹ {(Math.abs(formData.dueAmount) ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3 saleform">
                    <label htmlFor="paymentMode" className="form-label">
                      Payment Mode
                    </label>
                    <select
                      className="form-select"
                      id="paymentMode"
                      name="paymentMode"
                      value={formData.paymentMode}
                      onChange={handleInputChange}
                    >
                      <option value="cash">Cash</option>
                      <option value="bank">Bank Transfer</option>
                      <option value="upi">UPI</option>
                      <option value="card">Card</option>
                      <option value="credit">Credit</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-end">
            <button
              type="button"
              className="btn btn-custom me-2"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-custom">
              {id ? "Update" : "Save"} Purchase
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default headerLayoutWrapper(CreatePurchasePage);
